import * as echarts from "echarts";
import top from "@/components/chart/top.vue";
import footerCom from "@/components/chart/footer.vue";
import titleCom from "@/components/chart/title-nofoot.vue";
import longTitleCom from "@/components/chart/long-title-business.vue";
import logout from "@/components/chart/logout.vue";
import {
  getShopOptions,
  getRadarStallOptions,
  getBusinessOverview,
  getBusinessRadar,
  getDishTrend,
  getMealDetail,
  getStallRank,
  getRankDetail
} from "@/api/business-analysis";

const defaultSortIcon = "https://dobaycdn.anasit.com/dashboard_images/morrpxun.png"
const ascSortIcon = "https://dobaycdn.anasit.com/dashboard_images/shengxuxux.png"
const descSortIcon = "https://dobaycdn.anasit.com/dashboard_images/jiangxu.png"
const rankLineIcon = "https://dobaycdn.anasit.com/dashboard_images/dott.png"

export default {
  name: "BusinessAnalysis",
  components: {
    top,
    footerCom,
    titleCom,
    longTitleCom,
    logout,
  },
  data() {
    return {
      periodOptions: [
        { label: "日", value: "day" },
        { label: "周", value: "week" },
        { label: "月", value: "month" }
      ],
      overviewPeriodType: "month",
      overviewMetrics: [],
      radarDimension: "shop",
      radarShopIds: [],
      radarStallShopId: "",
      radarStallIds: [],
      radarItems: [],
      radarStallOptions: [],
      shopOptions: [],
      dishDimension: "shop",
      dishTopN: 5,
      dishTrend: { xAxis: [], series: [], currentShopIds: [] },
      dishTrendSwitching: false,
      dishTrendRequestId: 0,
      mealSections: [
        { key: "breakfast", title: "早餐Top10", ref: "breakfastChart" },
        { key: "lunch", title: "午餐Top10", ref: "lunchChart" },
        { key: "dinner", title: "晚餐Top10", ref: "dinnerChart" }
      ],
      mealDetail: {},
      rankDimension: "shop",
      rankPeriodType: "month",
      rankShopId: "",
      rankTopN: 10,
      rankBaseRows: [],
      rankRows: [],
      rankChart: { xAxis: [], sales: [], revenues: [] },
      rankSeriesVisible: {
        sales: true,
        revenue: true
      },
      rankSortField: "sales",
      rankSortOrder: "desc",
      detailVisible: false,
      detailTitle: "",
      detailRows: [],
      detailTotal: 0,
      detailPageNum: 1,
      detailPageSize: 10,
      detailSortField: "time",
      detailSortOrder: "asc",
      detailTarget: null,
      charts: {},
      radarColors: ["#4EA1FF", "#6EE7F4", "#65C96B", "#F0C22E", "#E9742C"],
      widthPix: 1,
      resizeHandler: null,
      refreshTimer: null
    };
  },
  mounted() {
    this.updateWidthPix();
    this.resizeHandler = () => {
      this.updateWidthPix();
      Object.keys(this.charts).forEach(key => {
        this.charts[key] && this.charts[key].resize();
      });
    };
    window.addEventListener("resize", this.resizeHandler);
    this.loadOptions().then(() => {
      this.refreshAll();
      this.refreshTimer = setInterval(this.refreshAll, 15 * 60 * 1000);
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeHandler);
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    Object.keys(this.charts).forEach(key => {
      this.charts[key] && this.charts[key].dispose();
    });
  },
  methods: {
    updateWidthPix() {
      this.widthPix = window.screen.width < 1920 ? 1 : Math.floor(window.screen.width / 1920);
    },
    chartFont(size) {
      return size * this.widthPix;
    },
    responseData(res, fallback) {
      if (!res) {
        return fallback;
      }
      return res.data === undefined ? fallback : res.data;
    },
    async loadOptions() {
      const shops = await getShopOptions();
      this.shopOptions = this.responseData(shops, []);
      this.radarStallOptions = [];
    },
    async loadRadarStallOptions() {
      const params = {};
      if (this.radarStallShopId) {
        params.shopId = this.radarStallShopId;
      }
      const res = await getRadarStallOptions(params);
      this.radarStallOptions = this.responseData(res, []);
    },
    refreshAll() {
      this.loadOverview();
      this.loadRadar();
      this.loadDishTrend();
      this.loadStallRank();
    },
    async loadOverview() {
      const res = await getBusinessOverview({ periodType: this.overviewPeriodType });
      const data = this.responseData(res, { metrics: [] });
      this.overviewMetrics = data.metrics || [];
    },
    // 雷达图筛选较多：食堂模式传多选食堂，档口模式传单选食堂和多选档口。
    async loadRadar() {
      const params = {
        periodType: this.overviewPeriodType,
        dimension: this.radarDimension
      };
      if (this.radarDimension === "shop") {
        params.radarSelectMode = this.radarShopIds.length ? "manual" : "auto";
        params.radarShopIds = this.radarShopIds;
      } else {
        params.radarSelectMode = this.radarStallIds.length ? "manual" : "auto";
        params.radarShopIds = this.radarStallShopId ? [this.radarStallShopId] : [];
        params.radarStallIds = this.radarStallIds;
      }
      const res = await getBusinessRadar(params);
      const data = this.responseData(res, { radarItems: [] });
      this.radarItems = (data.radarItems || data.items || []).map(item => ({
        ...item,
        name: item.name || item.rankName,
        rankKey: item.rankKey || String(item.rankId),
        compositeScore: item.compositeScore || item.comprehensiveScore
      }));
      this.$nextTick(this.drawRadarChart);
    },
    // 菜品热榜返回的 currentShopIds 会继续作为三餐明细的食堂过滤条件。
    async loadDishTrend() {
      const requestId = ++this.dishTrendRequestId;
      const res = await getDishTrend({
        dimension: this.dishDimension,
        topN: this.dishTopN
      });
      if (requestId !== this.dishTrendRequestId) {
        return;
      }
      this.dishTrend = this.responseData(res, { xAxis: [], series: [], currentShopIds: [] });
      this.dishTrendSwitching = false;
      await this.$nextTick();
      this.drawDishTrendChart();
      this.loadMealDetail();
    },
    async loadMealDetail() {
      const params = {
        dimension: this.dishDimension,
        shopIds: this.dishDimension === "shop" ? this.dishTrend.currentShopIds || [] : []
      };
      const res = await getMealDetail(params);
      this.mealDetail = this.responseData(res, {});
      this.$nextTick(this.drawMealCharts);
    },
    async loadStallRank() {
      const res = await getStallRank({
        rankType: this.rankDimension,
        periodType: this.rankPeriodType,
        topN: this.rankTopN,
        shopIds: this.rankDimension === "stall" && this.rankShopId ? [this.rankShopId] : [],
        sortField: "sales",
        sortOrder: "desc"
      });
      const data = this.responseData(res, { rows: [], xAxis: [], sales: [], revenues: [] });
      this.rankBaseRows = (data.rows || []).map((row, index) => ({
        ...row,
        originalRank: Number(row.rank || index + 1)
      }));
      this.applyRankTableSort();
      this.updateRankChartFromRows();
      this.$nextTick(this.drawStallRankChart);
    },
    // 详情弹窗由雷达图表格和档口排名表格共用，通过 detailTarget 保留当前对象和周期。
    async loadRankDetail() {
      if (!this.detailTarget) {
        return;
      }
      const res = await getRankDetail({
        periodType: this.detailTarget.periodType,
        rankType: this.detailTarget.rankType,
        rankId: this.detailTarget.rankId,
        shopId: this.detailTarget.shopId,
        stallId: this.detailTarget.stallId,
        detailSource: this.detailTarget.source,
        pageNum: this.detailPageNum,
        pageSize: this.detailPageSize,
        sortField: this.detailSortField,
        sortOrder: this.detailSortOrder
      });
      const data = this.responseData(res, { records: [], total: 0, title: "" });
      this.detailTitle = data.title || this.detailTitle;
      this.detailRows = data.records || data.rows || [];
      this.detailTotal = data.total || 0;
    },
    async changeOverviewPeriod(value) {
      this.overviewPeriodType = value;
      this.loadOverview();
      this.loadRadar();
    },
    async changeRadarDimension(value) {
      this.radarDimension = value;
      if (value === "shop") {
        this.radarStallShopId = "";
        this.radarStallIds = [];
      } else {
        this.radarShopIds = [];
        await this.loadRadarStallOptions();
      }
      this.loadRadar();
    },
    changeRadarShop(value) {
      if (value.length > 3) {
        this.radarShopIds = value.slice(0, 3);
      }
      this.loadRadar();
    },
    async changeRadarStallShop(value) {
      this.radarStallIds = [];
      await this.loadRadarStallOptions();
      this.loadRadar();
    },
    changeRadarStall(value) {
      if (value.length > 3) {
        this.radarStallIds = value.slice(0, 3);
      }
      this.loadRadar();
    },
    changeDishDimension(value) {
      if (this.dishDimension === value) {
        return;
      }
      this.startDishTrendSwitch();
      this.dishDimension = value;
      this.dishTopN = 5;
      this.loadDishTrend();
    },
    changeDishTop(value) {
      if (this.dishTopN === value) {
        return;
      }
      this.dishTopN = value;
      this.loadDishTrend();
    },
    startDishTrendSwitch() {
      this.dishTrendSwitching = true;
      this.dishTrend = { xAxis: [], series: [], currentShopIds: [] };
      if (this.charts.dishTrend) {
        this.charts.dishTrend.clear();
      }
    },
    changeRankTop(value) {
      if (this.rankTopN === value) {
        return;
      }
      this.rankTopN = value;
      this.loadStallRank();
    },
    changeRankDimension(value) {
      if (this.rankDimension === value) {
        return;
      }
      this.rankDimension = value;
      this.rankTopN = 10;
      this.rankShopId = "";
      this.rankSortField = "sales";
      this.rankSortOrder = "desc";
      this.rankBaseRows = [];
      this.rankRows = [];
      this.rankChart = { xAxis: [], sales: [], revenues: [] };
      this.$nextTick(this.drawStallRankChart);
      this.loadStallRank();
    },
    toggleRankSeries(type) {
      this.rankSeriesVisible[type] = !this.rankSeriesVisible[type];
      if (!this.rankSeriesVisible.sales && !this.rankSeriesVisible.revenue) {
        this.rankSeriesVisible[type] = true;
      }
      this.$nextTick(this.drawStallRankChart);
    },
    // 表格排序后同步重排上方图表，确保图表与表格展示顺序一致。
    applyRankTableSort() {
      const field = this.rankSortField;
      const order = this.rankSortOrder === "asc" ? 1 : -1;
      const rows = [...(this.rankBaseRows || [])];
      const numericFields = ["rank", "sales", "revenue", "ratio"];
      rows.sort((a, b) => {
        if (field === "name") {
          const result = String(a.name || "").localeCompare(String(b.name || ""), "zh-Hans-CN");
          return result * order || Number(a.originalRank || 0) - Number(b.originalRank || 0);
        }
        if (numericFields.includes(field)) {
          const aEmpty = a[field] === null || a[field] === undefined || a[field] === "";
          const bEmpty = b[field] === null || b[field] === undefined || b[field] === "";
          if (aEmpty !== bEmpty) {
            return aEmpty ? 1 : -1;
          }
          const aValue = field === "rank" ? Number(a.originalRank || 0) : Number(a[field] || 0);
          const bValue = field === "rank" ? Number(b.originalRank || 0) : Number(b[field] || 0);
          const result = aValue - bValue;
          return result * order || Number(a.originalRank || 0) - Number(b.originalRank || 0);
        }
        return Number(a.originalRank || 0) - Number(b.originalRank || 0);
      });
      this.rankRows = rows.map((row, index) => ({
        ...row,
        rank: index + 1
      }));
    },
    updateRankChartFromRows() {
      const rows = this.rankRows || [];
      this.rankChart = {
        xAxis: rows.map(item => item.name || ""),
        sales: rows.map(item => Number(item.sales || 0)),
        revenues: rows.map(item => Number(item.revenue || 0) / 10000)
      };
    },
    changeRankSort(field) {
      if (this.rankSortField === field) {
        this.rankSortOrder = this.rankSortOrder === "desc" ? "asc" : "desc";
      } else {
        this.rankSortField = field;
        this.rankSortOrder = "desc";
      }
      this.applyRankTableSort();
      this.updateRankChartFromRows();
      this.$nextTick(this.drawStallRankChart);
    },
    changeDetailSort(field) {
      if (this.detailSortField === field) {
        this.detailSortOrder = this.detailSortOrder === "desc" ? "asc" : "desc";
      } else {
        this.detailSortField = field;
        this.detailSortOrder = field === "time" ? "asc" : "desc";
      }
      this.detailPageNum = 1;
      this.loadRankDetail();
    },
    // 打开食堂/档口详情；source 用来区分使用雷达周期还是右侧排名周期。
    openRankDetail(rankType, item, source) {
      const rankId = rankType === "shop"
        ? (item.shopId || item.rankId || item.id)
        : (item.stallId || item.rankId || item.id);
      this.detailTarget = {
        rankType,
        rankId,
        shopId: item.shopId,
        stallId: item.stallId,
        source,
        periodType: source === "rank" ? this.rankPeriodType : this.overviewPeriodType
      };
      this.detailTitle = (rankType === "shop" ? "食堂详情 - " : "档口详情 - ") + item.name;
      this.detailRows = [];
      this.detailTotal = 0;
      this.detailPageNum = 1;
      this.detailSortField = "time";
      this.detailSortOrder = "asc";
      this.detailVisible = true;
      this.loadRankDetail();
    },
    chart(name, el) {
      if (!el) {
        return null;
      }
      if (!this.charts[name]) {
        this.charts[name] = echarts.init(el);
      }
      return this.charts[name];
    },
    // 雷达图显示销量、客单价、营收、订单数四个评分维度。
    drawRadarChart() {
      const chart = this.chart("radar", this.$refs.radarChart);
      if (!chart) {
        return;
      }
      const data = this.radarItems.map((item, index) => ({
        name: item.name,
        value: [
          Number(item.salesScore || 0),
          Number(item.orderCountScore || 0),
          Number(item.revenueScore || 0),
          Number(item.avgOrderAmountScore || 0)
        ],
        areaStyle: { opacity: 0.22 },
        lineStyle: { width: 2 },
        itemStyle: { color: this.radarColors[index % this.radarColors.length] }
      }));
      chart.setOption({
        tooltip: {
          trigger: "item",
          backgroundColor: "rgba(6, 13, 22, 0.94)",
          borderColor: "rgba(77, 108, 137, 0.45)",
          borderWidth: 1,
          padding: [6, 10],
          textStyle: { color: "#d6e3f2", fontSize: this.chartFont(11) },
          extraCssText: "box-shadow:0 0 12px rgba(0,0,0,.45);border-radius:3px;"
        },
        radar: {
          center: ["46%", "50%"],
          radius: "56%",
          splitNumber: 4,
          axisName: { color: "#dce9ff", fontSize: this.chartFont(11) },
          nameGap: 8,
          axisLine: { lineStyle: { color: "rgba(228,240,255,.75)" } },
          splitLine: { lineStyle: { color: "rgba(228,240,255,.75)" } },
          splitArea: { areaStyle: { color: ["rgba(11,35,64,.05)", "rgba(11,35,64,.16)"] } },
          indicator: [
            { name: "销量", max: 100 },
            { name: "订单数", max: 100 },
            { name: "营收", max: 100 },
            { name: "客单价", max: 100 }
          ]
        },
        series: [{ type: "radar", data }]
      }, true);
    },
    // 菜品热榜趋势图支持 dataZoom 横向滚动，适配近 30 天多点位数据。
    drawDishTrendChart() {
      const chart = this.chart("dishTrend", this.$refs.dishTrendChart);
      if (!chart) {
        return;
      }
      const colors = ["#F2C94C", "#4EA1FF", "#B9C7D8", "#6EE7F4", "#E9742C", "#65C96B"];
      const xAxisData = this.dishTrend.xAxis || [];
      const endValue = Math.min(xAxisData.length - 1, 15);
      const isMealTrend = this.dishDimension === "meal";
      const trendSeries = (this.dishTrend.series || []).map((item, index) => ({
        name: item.name,
        type: "line",
        smooth: true,
        showSymbol: true,
        symbol: "circle",
        symbolSize: 3,
        lineStyle: { width: 1.5, color: colors[index % colors.length] },
        itemStyle: { color: colors[index % colors.length] },
        data: (item.data || []).map(value => Number(value || 0))
      }));
      const maxSales = Math.max(0, ...trendSeries.reduce((values, item) => values.concat(item.data), []));
      const yAxisScale = this.buildTrendYAxis(maxSales);
      chart.resize();
      chart.clear();
      chart.setOption({
        color: colors,
        tooltip: {
          trigger: "axis",
          backgroundColor: "rgba(6, 13, 22, 0.94)",
          borderColor: "rgba(77, 108, 137, 0.45)",
          borderWidth: 1,
          padding: [6, 10],
          textStyle: { color: "#d6e3f2", fontSize: this.chartFont(11) },
          extraCssText: "box-shadow:0 0 12px rgba(0,0,0,.45);border-radius:3px;",
          position(point, params, dom, rect, size) {
            const x = Math.min(point[0] + 18, size.viewSize[0] - size.contentSize[0] - 8);
            const y = Math.min(point[1] + 18, size.viewSize[1] - size.contentSize[1] - 8);
            return [x, y];
          },
          formatter: this.formatTrendTooltip
        },
        legend: {
          top: 8,
          right: 0,
          width: 520,
          itemWidth: 14,
          itemHeight: 7,
          itemGap: 8,
          textStyle: { color: "#b9c9dc", fontSize: this.chartFont(12), lineHeight: this.chartFont(12) },
          data: (this.dishTrend.series || []).map(item => item.name)
        },
        grid: { left: 40, right: 20, top: isMealTrend ? 44 : 50, bottom: isMealTrend ? 34 : 40 },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: xAxisData,
          axisLabel: { color: "#d4dfec", interval: 0, fontSize: this.chartFont(11) },
          axisLine: { lineStyle: { color: "rgba(210,225,242,.35)" } }
        },
        yAxis: {
          type: "value",
          min: 0,
          max: yAxisScale.max,
          interval: yAxisScale.interval,
          axisLabel: { color: "rgba(212,223,236,.65)", fontSize: this.chartFont(11) },
          splitLine: { lineStyle: { type: "dashed", color: "rgba(210,225,242,.18)" } }
        },
        dataZoom: [
          {
            type: "slider",
            width: 70,
            height: 4,
            left: "center",
            bottom: 2,
            xAxisIndex: 0,
            startValue: 0,
            endValue,
            filterMode: "none",
            borderColor: "transparent",
            backgroundColor: "#183a64",
            fillerColor: "#e8eef7",
            dataBackground: {
              lineStyle: { opacity: 0 },
              areaStyle: { opacity: 0 }
            },
            selectedDataBackground: {
              lineStyle: { opacity: 0 },
              areaStyle: { opacity: 0 }
            },
            handleSize: 0,
            moveHandleSize: 0,
            showDetail: false,
            brushSelect: false
          },
          { type: "inside", xAxisIndex: 0, startValue: 0, endValue, filterMode: "none", zoomOnMouseWheel: false, moveOnMouseMove: true, moveOnMouseWheel: true }
        ],
        series: trendSeries
      }, true);
    },
    // 三餐明细分别绘制早、午、晚三个 Top10 柱状图。
    drawMealCharts() {
      const maxSales = Math.max(0, ...this.mealSections.reduce((values, section) => {
        const rows = this.mealDetail[section.key] || [];
        return values.concat(rows.map(item => Number(item.sales || 0)));
      }, []));
      const yAxisScale = this.buildTrendYAxis(maxSales);
      this.mealSections.forEach(section => {
        const ref = this.$refs[section.ref];
        const el = Array.isArray(ref) ? ref[0] : ref;
        const chart = this.chart(section.key, el);
        if (!chart) {
          return;
        }
        const rows = this.mealDetail[section.key] || [];
        chart.resize();
        chart.setOption(this.barOption(rows, yAxisScale), true);
      });
    },
    // 档口排名图表使用销量柱状图 + 销售额折线图，并支持横向滚动。
    drawStallRankChart() {
      const chart = this.chart("stallRank", this.$refs.stallRankChart);
      if (!chart) {
        return;
      }
      const xAxisData = this.rankChart.xAxis || [];
      const visibleCount = 10;
      const endValue = Math.max(0, Math.min(xAxisData.length - 1, visibleCount - 1));
      const salesData = this.rankSeriesVisible.sales ? (this.rankChart.sales || []) : [];
      const revenueData = this.rankSeriesVisible.revenue ? (this.rankChart.revenues || []) : [];
      chart.clear();
      chart.setOption({
        tooltip: {
          trigger: "axis",
          backgroundColor: "rgba(6, 13, 22, 0.94)",
          borderColor: "rgba(77, 108, 137, 0.45)",
          borderWidth: 1,
          padding: [6, 10],
          textStyle: { color: "#d6e3f2", fontSize: this.chartFont(11) },
          extraCssText: "box-shadow:0 0 12px rgba(0,0,0,.45);border-radius:3px;",
          formatter: params => this.formatRankTooltip(params)
        },
        grid: { left: 36, right: 34, top: 6, bottom: 78 },
        xAxis: {
          type: "category",
          data: xAxisData,
          axisLabel: {
            color: "#d4dfec",
            interval: 0,
            fontSize: this.chartFont(10),
            lineHeight: this.chartFont(11),
            formatter: value => {
              const chars = Array.from(String(value || ""));
              return chars.length > 4 ? chars.slice(0, 4).join("\n") + "\n..." : chars.join("\n");
            }
          },
          axisLine: { lineStyle: { color: "rgba(210,225,242,.35)" } }
        },
        yAxis: [
          {
            type: "value",
            splitNumber: 4,
            axisLabel: { color: "rgba(212,223,236,.65)", fontSize: this.chartFont(11) },
            splitLine: { lineStyle: { type: "dashed", color: "rgba(210,225,242,.18)" } }
          },
          {
            type: "value",
            splitNumber: 4,
            axisLabel: { color: "rgba(212,223,236,.65)", fontSize: this.chartFont(11) },
            splitLine: { show: false }
          }
        ],
        dataZoom: [
          {
            type: "slider",
            width: 70,
            height: 3,
            left: "center",
            bottom: 8,
            xAxisIndex: 0,
            startValue: 0,
            endValue,
            filterMode: "none",
            borderColor: "transparent",
            backgroundColor: "#183a64",
            fillerColor: "#e8eef7",
            dataBackground: {
              lineStyle: { opacity: 0 },
              areaStyle: { opacity: 0 }
            },
            selectedDataBackground: {
              lineStyle: { opacity: 0 },
              areaStyle: { opacity: 0 }
            },
            handleSize: 0,
            moveHandleSize: 0,
            showDetail: false,
            brushSelect: false
          },
          { type: "inside", xAxisIndex: 0, startValue: 0, endValue, filterMode: "none", zoomOnMouseWheel: false, moveOnMouseMove: true, moveOnMouseWheel: true }
        ],
        series: [
          {
            name: "销量",
            type: "bar",
            barWidth: 12,
            data: salesData,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "#75BEFF" },
                { offset: 1, color: "#183C74" }
              ])
            }
          },
          {
            name: "销售额",
            type: "line",
            yAxisIndex: 1,
            smooth: true,
            symbol: "circle",
            symbolSize: 4,
            data: revenueData,
            lineStyle: { color: "#E5EAF1", width: 2 },
            itemStyle: { color: "#E5EAF1" }
          }
        ]
      }, true);
    },
    barOption(rows, yAxisScale) {
      return {
        tooltip: {
          trigger: "axis",
          appendToBody: true,
          confine: false,
          backgroundColor: "rgba(6, 13, 22, 0.94)",
          borderColor: "rgba(77, 108, 137, 0.45)",
          borderWidth: 1,
          padding: [6, 10],
          textStyle: { color: "#d6e3f2", fontSize: this.chartFont(11) },
          extraCssText: "box-shadow:0 0 12px rgba(0,0,0,.45);border-radius:3px;",
          formatter: params => this.formatRankTooltip(params, "销量 (份):")
        },
        grid: { left: 38, right: 12, top: 8, height: 82 },
        xAxis: {
          type: "category",
          data: rows.map(item => item.name),
          axisLabel: {
            color: "#c8d6e6",
            interval: 0,
            fontSize: this.chartFont(10),
            lineHeight: this.chartFont(11),
            formatter: value => {
              const text = String(value || "");
              const maxChars = 8;
              const label = text.length > maxChars ? `${text.slice(0, maxChars).split("").join("\n")}\n...` : text.split("").join("\n");
              return label;
            }
          },
          axisLine: { lineStyle: { color: "rgba(210,225,242,.35)" } }
        },
        yAxis: {
          type: "value",
          max: yAxisScale.max,
          interval: yAxisScale.interval,
          axisLabel: { color: "rgba(212,223,236,.65)", fontSize: this.chartFont(11) },
          splitLine: { lineStyle: { type: "dashed", color: "rgba(210,225,242,.18)" } }
        },
        series: [{
          type: "bar",
          barWidth: 12,
          data: rows.map(item => item.sales),
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "#75BEFF" },
              { offset: 1, color: "#183C74" }
            ])
          }
        }]
      };
    },
    sortIcon(field) {
      if (this.rankSortField !== field) {
        return defaultSortIcon;
      }
      return this.rankSortOrder === "asc" ? ascSortIcon : descSortIcon;
    },
    detailSortIcon(field) {
      if (this.detailSortField !== field) {
        return defaultSortIcon;
      }
      return this.detailSortOrder === "asc" ? ascSortIcon : descSortIcon;
    },
    buildTrendYAxis(maxValue) {
      const value = Number(maxValue || 0);
      if (value <= 0) {
        return { max: 100, interval: 20 };
      }
      const targetSplitCount = 6;
      const paddedMax = value * 1.08;
      const interval = this.niceCeil(paddedMax / targetSplitCount);
      return {
        max: interval * Math.ceil(paddedMax / interval),
        interval
      };
    },
    niceCeil(value) {
      const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
      const normalized = value / magnitude;
      const steps = [1, 2, 2.5, 3, 5, 10];
      const step = steps.find(item => normalized <= item) || 10;
      return step * magnitude;
    },
    rankTooltipMarker(item) {
      if (item.seriesType === "line") {
        const width = this.chartFont(16);
        return `<span style="display:inline-block;width:${width}px;height:${this.chartFont(6)}px;flex:0 0 ${width}px;background:url(${rankLineIcon}) center/100% 100% no-repeat;"></span>`;
      }
      const width = this.chartFont(14);
      return `<span style="display:inline-block;width:${width}px;height:${this.chartFont(2)}px;flex:0 0 ${width}px;background:#69aaf7;"></span>`;
    },
    formatRankTooltip(params, valueLabel) {
      const items = Array.isArray(params) ? params : [];
      if (!items.length) {
        return "";
      }
      const rows = items.map(item => {
        const seriesName = String(item.seriesName || "").trim();
        const label = valueLabel || seriesName;
        const isRevenue = seriesName === "销售额" || item.seriesType === "line";
        const rawValue = Array.isArray(item.value) ? item.value[item.value.length - 1] : item.value;
        const value = Number(rawValue || 0);
        const text = isRevenue ? value.toFixed(2) : this.formatNumber(value);
        const marker = this.rankTooltipMarker(item);
        return `
          <div style="display:flex;align-items:center;justify-content:space-between;gap:${this.chartFont(18)}px;line-height:${this.chartFont(20)}px;">
            <span style="display:inline-flex;align-items:center;gap:${this.chartFont(7)}px;">${marker}<span>${label}</span></span>
            <b style="color:#dfe9f7;">${text}</b>
          </div>`;
      }).join("");
      return `
        <div style="min-width:130px;">
          <div style="font-weight:700;color:#fff;margin-bottom:4px;">${items[0].axisValue || ""}</div>
          ${rows}
        </div>`;
    },
    formatTrendTooltip(params) {
      const items = Array.isArray(params) ? params : [];
      if (!items.length) {
        return "";
      }
      const rows = items.map(item => {
        const color = item.color || "#63b7ff";
        return `
          <div style="display:flex;align-items:center;gap:${this.chartFont(7)}px;line-height:${this.chartFont(20)}px;">
            <span style="position:relative;display:inline-block;width:16px;height:8px;flex:0 0 16px;">
              <span style="position:absolute;left:0;right:0;top:3px;height:2px;background:${color};"></span>
              <span style="position:absolute;left:5px;top:1px;width:6px;height:6px;border-radius:50%;background:${color};"></span>
            </span>
            <span style="min-width:${this.chartFont(100)}px;color:#b8c5d4;font-size:${this.chartFont(11)}px;">${item.seriesName}</span>
            <span style="min-width:${this.chartFont(60)}px;text-align:right;color:#dce7f4;font-size:${this.chartFont(11)}px;">${this.formatNumber(item.value)}</span>
          </div>
        `;
      }).join("");
      return `
        <div style="min-width:172px;">
          <div style="font-size:${this.chartFont(13)}px;font-weight:700;color:#edf4ff;line-height:${this.chartFont(23)}px;border-bottom:1px solid rgba(120,150,180,.18);margin-bottom:4px;">${items[0].axisValue}</div>
          ${rows}
        </div>
      `;
    },
    formatMetricValue(item) {
      const value = Number(item.value || 0);
      if (item.code === "avgOrderAmount") {
        return value.toFixed(2);
      }
      return (value / 10000).toFixed(2);
    },
    formatRatio(value) {
      if (value === null || value === undefined) {
        return "--";
      }
      const num = Number(value);
      return (num >= 0 ? "▲" : "▼") + Math.abs(num).toFixed(2) + "%";
    },
    formatArrowRatio(value) {
      if (value === null || value === undefined) {
        return "--";
      }
      const num = Number(value);
      return (num >= 0 ? "▲" : "▼") + Math.abs(num).toFixed(2) + "%";
    },
    formatScore(value) {
      return Math.round(Number(value || 0));
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString();
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2);
    },
    formatWan(value) {
      return (Number(value || 0) / 10000).toFixed(2) + " 万";
    }
  }
};
